// src/theme.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

export type ThemeMode = "purple" | "dark" | "maroon";
export type EditorMode = "white" | "black";

type ThemeContextValue = {
  theme: ThemeMode;
  editorMode: EditorMode;
  setTheme: (t: ThemeMode) => void;
  setEditorMode: (m: EditorMode) => void;
  isDark: boolean;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = "thesiscribe_theme";
const EDITOR_KEY = "thesiscribe_editor_mode";

// ----- Provider -----
const ThemeProviderImpl = (props: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<ThemeMode>("purple");
  const [editorMode, setEditorMode] = useState<EditorMode>("black");

  // Load saved values once
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem(THEME_KEY) as
        | ThemeMode
        | "light"
        | null;
      const savedEditor = localStorage.getItem(EDITOR_KEY) as EditorMode | null;

      if (savedTheme === "light") {
        setTheme("maroon");
      } else if (savedTheme === "purple" || savedTheme === "dark" || savedTheme === "maroon") {
        setTheme(savedTheme);
      }
      if (savedEditor === "white" || savedEditor === "black") {
        setEditorMode(savedEditor);
      }
    } catch {
      // ignore
    }
  }, []);

  // Persist theme
  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  // Persist editor mode
  useEffect(() => {
    try {
      localStorage.setItem(EDITOR_KEY, editorMode);
    } catch {
      // ignore
    }
  }, [editorMode]);

  const value: ThemeContextValue = {
    theme,
    editorMode,
    setTheme,
    setEditorMode,
    isDark: theme === "purple" || theme === "dark",
  };

  return (
    <ThemeContext.Provider value={value}>
      {props.children}
    </ThemeContext.Provider>
  );
};

// Export with type assertion to avoid generics markup issues
export const ThemeProvider = ThemeProviderImpl as (
  props: { children: React.ReactNode }
) => JSX.Element;

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
}